import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../brand/BrandLogo';
import BookNowLink from '../booking/BookNowLink';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import ThemeToggle from '../ui/ThemeToggle';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import HeaderSignOutButton from '../layout/HeaderSignOutButton';
import ClientSessionLayout from '../layout/ClientSessionLayout';

const MarketplacePublicLayout = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { itemCount } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  const CartLink = ({ className }) => (
    <Link to="/client/cart" className={className} onClick={closeMenu}>
      {t('common.cart')}
      {itemCount > 0 && (
        <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-glow-600 px-1 text-xs text-white">
          {itemCount}
        </span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen min-w-0 overflow-x-hidden bg-zinc-50 dark:bg-zinc-950">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-zinc-50/90 backdrop-blur dark:border-zinc-800 dark:bg-zinc-950/90">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <BrandLogo to="/marketplace" size="sm" showWordmark onClick={closeMenu} />

          <div className="hidden items-center gap-4 md:flex">
            <LanguageSwitcher />
            <ThemeToggle />
            <Link
              to="/explore"
              className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
            >
              {t('marketplace.exploreSalons')}
            </Link>
            <BookNowLink className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white">
              {t('common.book')}
            </BookNowLink>
            <CartLink className="relative text-sm text-zinc-700 hover:text-zinc-900 dark:text-zinc-300 dark:hover:text-white" />
            {isAuthenticated ? (
              <>
                <Link
                  to="/client/dashboard"
                  className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
                >
                  {t('common.dashboard')}
                </Link>
                <HeaderSignOutButton className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white" />
              </>
            ) : (
              <Link to="/login" className="rounded-lg bg-glow-600 px-3 py-1.5 text-sm text-white">
                {t('common.signIn')}
              </Link>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <CartLink className="relative rounded-lg px-2 py-1.5 text-sm text-zinc-700 dark:text-zinc-300" />
            <button
              type="button"
              className="rounded-lg p-2 text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
              aria-label={menuOpen ? t('common.closeMenu') : t('common.openMenu')}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {menuOpen && (
          <>
            <button
              type="button"
              className="fixed inset-0 z-40 bg-black/40 md:hidden"
              aria-label={t('common.closeMenu')}
              onClick={closeMenu}
            />
            <div className="absolute inset-x-0 top-full z-50 border-b border-zinc-200 bg-zinc-50 p-4 shadow-lg dark:border-zinc-800 dark:bg-zinc-950 md:hidden">
              <nav className="flex flex-col gap-2">
                <LanguageSwitcher className="w-full" selectClassName="w-full" />
                <ThemeToggle className="w-full justify-center" />
                <Link
                  to="/explore"
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900"
                >
                  {t('marketplace.exploreSalons')}
                </Link>
                <BookNowLink
                  onClick={closeMenu}
                  className="rounded-lg px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900"
                >
                  {t('common.book')}
                </BookNowLink>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/client/dashboard"
                      onClick={closeMenu}
                      className="rounded-lg px-3 py-2.5 text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900"
                    >
                      {t('common.dashboard')}
                    </Link>
                    <HeaderSignOutButton className="w-full rounded-lg px-3 py-2.5 text-left text-sm text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900" />
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={closeMenu}
                    className="rounded-lg bg-glow-600 px-3 py-2.5 text-center text-sm text-white"
                  >
                    {t('common.signIn')}
                  </Link>
                )}
              </nav>
            </div>
          </>
        )}
      </header>
      {children}
    </div>
  );
};

const MarketplaceLayout = ({ children }) => (
  <ClientSessionLayout publicLayout={<MarketplacePublicLayout>{children}</MarketplacePublicLayout>}>
    <div className="min-h-full min-w-0 bg-zinc-50 dark:bg-zinc-950">{children}</div>
  </ClientSessionLayout>
);

export default MarketplaceLayout;
